import type { ConfigOf } from "@p0security/shared/install/types";
import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

import {
  validateDomainId,
  validateFingerprint,
  validatePrivateKey,
  validateRegion,
  validateTenancyId,
  validateUserId,
} from "./credentials.ts";

const iamLoginOption = Option({
  maturity: "preview" as const,
  label: "As Oracle IAM users",
  schema: {
    identity: Element({
      label: "How are users identified?",
      type: "select",
      default: "email",
      options: {
        email: Option({
          maturity: "preview" as const,
          label: "User name is user email",
          schema: {},
        }),
      },
    }),
  },
});

export const loginOptions = {
  iam: iamLoginOption,
};

export const OracleComponents = InstallSpec({
  "iam-write": ItemComponent({
    label: "IAM management",
    description:
      "Allows P0 to grant and revoke Oracle Cloud Infrastructure access for your users. Required for just-in-time access.",
    type: "string",
    maturity: "preview",
    schema: {
      /**
       * OCI only supports managing users who are already present in OCI IAM.
       *
       * TODO: Support federated users via OCI Identity Federation (e.g. via OCI Identity Providers and
       * SAML2 connections).
       */
      login: Element({
        label: "How are users provisioned?",
        type: "select" as const,
        default: "iam",
        options: loginOptions,
      }),
      /**
       * P0 signs OCI API calls with an API key of a service user the customer
       * creates for P0.
       *
       * TODO: move to workload identity federation to avoid managing API keys directly.
       */
      tenancyId: Element({
        type: "string",
        label: "Tenancy OCID",
        description:
          "The OCID of your tenancy, from the Tenancy details page in the Oracle Cloud console",
        validator: validateTenancyId,
      }),
      userId: Element({
        type: "string",
        label: "P0 service user OCID",
        description: "The OCID of the user you created for P0",
        validator: validateUserId,
      }),
      fingerprint: Element({
        type: "string",
        label: "API key fingerprint",
        description:
          "The fingerprint Oracle Cloud shows after you add the API key to the P0 service user",
        validator: validateFingerprint,
      }),
      apiPrivateKey: Element({
        type: "encrypted",
        label: "API private key",
        description:
          "The contents of the private key file for the API key, including the BEGIN and END lines",
        validator: validatePrivateKey,
      }),
      /**
       * The base64-encoded PEM key of installs made before the key moved to
       * `apiPrivateKey`. Read only when `apiPrivateKey` is absent.
       */
      privateKey: Element({
        type: "hidden",
        label: "Legacy API private key",
        optional: true,
      }),
      region: Element({
        type: "string",
        label: "Home region",
        description:
          "The region identifier of your tenancy's home region, such as us-ashburn-1",
        validator: validateRegion,
      }),
    },
    validator: validateDomainId,
  }),
});

export type OracleUse = keyof typeof OracleComponents;
export type OracleIntegration = ConfigOf<typeof OracleComponents>;
export type OracleIntegrationConfig = OracleIntegration["iam-write"]["config"];
